import React, { useRef, useState, useEffect, useMemo } from 'react';
import { makeObservable, observable, action } from 'mobx';
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { MapContainer, TileLayer, useMap, useMapEvents, Polyline, Polygon, Marker, Popup } from 'react-leaflet';
import { HeatmapLayer } from 'react-leaflet-heatmap-layer-v3';
import ReactSlider from 'react-slider';
import { IoPlaySharp, IoPause, IoCloseSharp } from 'react-icons/io5';
import { BsQuestionLg } from 'react-icons/bs';
import { PiGraph, PiLineVerticalLight } from 'react-icons/pi';
import { LuLayers } from 'react-icons/lu';
import { IoMdCheckmark, IoMdUndo } from 'react-icons/io';
import { BiRename } from 'react-icons/bi';
import { RiDeleteBin6Line } from 'react-icons/ri';
import Form from 'react-bootstrap/Form';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { Typeahead } from 'react-bootstrap-typeahead';
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Label, Legend, ReferenceLine, ReferenceDot, Text } from 'recharts';
import * as Help from './Epidemics_Help.jsx';
import { transliterate } from 'transliteration';
import { getEpidemics_Infection_List, getEpidemics_Town_List, getEpidemics_Town_Convex_Hulls, getEpidemics_Town_Boundaries } from "../../http/Data_API/Epidemics_Data_API";
import { getEpidemics_Calculation_List, getEpidemics_Calculation_Metadata, getEpidemics_Calculation_Data, performEpidemics_Calculation } from "../../http/Data_API/Epidemics_Data_API";
import { renameEpidemics_Calculation, deleteEpidemics_Calculation } from '../../http/Data_API/Epidemics_Data_API';
import { Icon } from "leaflet";
import 'leaflet/dist/leaflet.css';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import './Epidemics_Map_Page.css';

class UI {
    toolbar_window = "ModelSelection"

    model = {
        name: "",
        type: "dSEIR",
        infection: "other",
        town: false,
        outbreak: false,
        dSEIR_R0: false,
        dSEIR_tE: false,
        dSEIR_tI: false,
        dSEIR_beta: false,
        dSEIR_sigma: false,
        dSEIR_gamma: false,
        dSEIR_radius: 1
    }

    location = {
        town_center: [56.326797, 44.006516],
        town_boundaries: false,
    }

    infections = false
    infection_parameters = false

    towns = false
    by_osm_rel = false

    constructor() {
        makeObservable(this, {
            toolbar_window: observable,
            model: observable,
            location: observable,
            towns: observable,
            by_osm_rel: observable,
            openHelpWindow: action,
            openModelSelectionWindow: action,
            openLayersWindow: action,
            closeToolbarWindows: action,
            setModelParam: action,
            setLocation: action,
            setInfections: action,
            setTowns: action,
            unloadTowns: action,
            unloadModel: action
        })
    }

    openHelpWindow() {
        this.toolbar_window = "Help";
    }

    openModelSelectionWindow() {
        this.toolbar_window = "ModelSelection";
    }

    openLayersWindow() {
        this.toolbar_window = "Layers";
    }

    closeToolbarWindows() {
        this.toolbar_window = "";
    }

    setModelParam(param, value) {
        this.model[param] = value;
    }

    setLocation(param, value) {
        this.location[param] = value;
    }

    setInfections(json) {
        this.infections = json;
    }

    setTowns(json) {
        this.towns = json;
        this.by_osm_rel = {};
        for (const [i, town] of json.entries()) this.by_osm_rel[town["town_osm_rel"]] = i;
    }

    unloadTowns() {
        this.towns = false;
        this.by_osm_rel = false;
    }

    unloadModel() {
        for (const param in this.model) this.model[param] = false;
        this.model["name"] = "";
        this.model["type"] = "dSEIR";
        this.model["infection"] = "other";
        this.model["dSEIR_radius"] = 1;
        this.location["town_boundaries"] = false;
    }
}

class Animation {
    playing = false;
    timer = false;
    layer = false;
    frame = 1;
    maxFrame = 1;

    constructor() {
        makeObservable(this, {
            playing: observable,
            timer: observable,
            layer: observable,
            setLayer: action,
            frame: observable,
            setFrame: action,
            maxFrame: observable,
            setMaxFrame: action,
            unload: action
        })
    }

    setLayer = (layer) => this.layer = layer;
    setFrame = (frame) => this.frame = frame;
    setMaxFrame = (maxFrame) => this.maxFrame = maxFrame;

    unload() {
        this.layer = false;
        this.frame = 1;
        this.maxFrame = 1;
    }
}

class Calculation {
    loading = false
    id = 0
    status = {
        status: "done",
        delete: false,
        time_remaining: false,
        frame_num: false,
        name: false
    }
    layers = []
    default_layer = false
    data = false
    error_message = ""

    constructor() {
        makeObservable(this, {
            loading: observable,
            id: observable,
            status: observable,
            layers: observable,
            default_layer: observable,
            error_message: observable,
            setId: action,
            setStatus: action,
            setLayers: action,
            loadData: action,
            unloadData: action,
            unload: action
        })
    }

    setId(id) {
        this.id = id;
    }

    setStatus(param, value) {
        if (param == "status" && value == "error") {
            const prev_status = this.status[param];
            this.status[param] = value;
            const timer = setInterval(() => {this.status[param] = prev_status; clearInterval(timer);}, 3000);
        }
        else this.status[param] = value; 
    }

    setLayers(layers) {
        this.layers = layers;
    }

    setDefaultLayer(layer) {
        this.default_layer = layer;
    }

    loadData(data) {
        this.data = data;
    }

    unloadData() {
        this.data = false;
    }

    unload() {
        this.id = 0;
        this.status["delete"] = false;
        this.status["time_remaining"] = false;
        this.status["frame_num"] = false;
        this.status["name"] = false;
        this.layers = [];
        this.default_layer = false;
        this.data = false;
        this.error_message = "";
    }
}

const animation = new Animation();
const ui = new UI();
const calculation = new Calculation();

const loadCalculationMetadata = async (id) => {
    let json;
    if (id === 0) json = await getEpidemics_Calculation_Metadata();
    else json = await getEpidemics_Calculation_Metadata(id);
    if (json && json?.calculation_metadata) {
        for (const model_param of ["infection", "town", "dSEIR_beta", "dSEIR_sigma", "dSEIR_gamma", "dSEIR_radius"])
            ui.setModelParam(model_param, json["calculation_metadata"]["параметры_модели"][model_param]);
        if (json["calculation_metadata"]["параметры_модели"]["outbreak"][0] != null)
            ui.setModelParam("outbreak", json["calculation_metadata"]["параметры_модели"]["outbreak"]);
        ui.setLocation("town_center", json["calculation_metadata"]["параметры_модели"]["center"]);
        ui.setLocation("town_boundaries", json["calculation_metadata"]["параметры_модели"]["boundaries"]);
        calculation.setId(json["calculation_id"]);
        calculation.setStatus("status", json["status"]);
        calculation.setStatus("name", json["calculation_name"]);
        calculation.setDefaultLayer(json["calculation_metadata"]["параметры_модели"]["default_layer"]);
        if (json["status"] == "in_progress") {
            calculation.setStatus("frame_num", json["frame_num"]);
            if (json["time_remaining"]) calculation.setStatus("time_remaining", json["time_remaining"]);
        }
        else calculation.setStatus("frame_num", false);
        calculation.setLayers(json["calculation_metadata"]["параметры_слоев"]);
    }
    else {
        ui.unloadModel();
        animation.unload();
        calculation.unload();
        calculation.setId(0);
    }
}

const loadCalculationData = async(id) => {
    calculation.unloadData();
    calculation.loading = true;
    const json = await getEpidemics_Calculation_Data(id);
    if (json && json?.calculation_data) {
    calculation.loadData(json["calculation_data"]);
    animation.setMaxFrame(calculation.data["динамика_по_точкам"][0][2][0].length);
    animation.setLayer(calculation.default_layer);
    calculation.loading = false;
    }
    else calculation.setStatus("status", "error");
}

const FrameSlider = observer(() => {
    const map = useMap();
    const pan_disable = useRef();
    const { t } = useTranslation();

    useEffect(() => {
        pan_disable.current.addEventListener("mouseenter", () => {map.doubleClickZoom.disable(); map.scrollWheelZoom.disable(); map.dragging.disable();});
        pan_disable.current.addEventListener("mouseleave", () => {map.doubleClickZoom.enable(); map.scrollWheelZoom.enable(); map.dragging.enable();});
        return () => {};
    }, [pan_disable]);

    function animation_play_pause() {
        if (animation.playing == false) {
            if (animation.frame == animation.maxFrame) animation.setFrame(1);
            animation.playing = true;
            animation.timer = setInterval(() => animation.setFrame(animation.frame + 1), 66);
        }
        else {
            animation.playing = false;
            clearInterval(animation.timer);
        }
    }

    if (animation.frame == animation.maxFrame) {
        animation.playing = false;
        clearInterval(animation.timer);
    }

    return (
        <div className="leaflet-top leaflet-left" id="выбор_кадра_рамка_1">
            <div className="leaflet-control leaflet-bar" id="выбор_кадра_рамка_2" ref={pan_disable}>
                <div id="выбор_кадра_таблица"><div id="выбор_кадра_строка">
                    <div id="выбор_кадра_ячейка_кнопки" role="button" onClick={animation_play_pause} title={animation.playing ? t("epidemics_pause_button") : t("epidemics_play_button")} aria-label={animation.playing ? t("epidemics_pause_button") : t("epidemics_play_button")}>
                    {animation.playing ? <IoPause size={18} className="выбор_кадра_кнопка" /> : <IoPlaySharp size={18} className={"выбор_кадра_кнопка " + (calculation.data == false && "выбор_кадра_кнопка_неактивная")} />}</div>
                    <div id="выбор_кадра_ячейка_слайдера">
                        <ReactSlider
                            ariaLabel={t("epidemics_day")}
                            className="выбор_кадра_слайдер"
                            disabled={calculation.data == false}
                            min={1}
                            max={animation.maxFrame}
                            thumbClassName={calculation.data ? "выбор_кадра_слайдер_ползунок" : "invisible"}
                            trackClassName="выбор_кадра_слайдер_дорожка"
                            value={animation.frame}
                            onChange={(value) => {animation.setFrame(value);}}
                            renderThumb={(props, state) => <div title={t("epidemics_day")} {...props}>{calculation.data !== false ? state.valueNow : ""}</div>} />
                    </div>
                </div></div>
            </div>
        </div>
    );
});

const Toolbar = observer(() => {
    const map = useMap();
    const pan_disable = useRef();
    const { t } = useTranslation();

    useEffect(() => {
        pan_disable.current.addEventListener("mouseenter", () => {map.doubleClickZoom.disable(); map.scrollWheelZoom.disable(); map.dragging.disable();});
        pan_disable.current.addEventListener("mouseleave", () => {map.doubleClickZoom.enable(); map.scrollWheelZoom.enable(); map.dragging.enable();});
        return () => {};
    }, [pan_disable]);

    return (
        <div className="leaflet-top leaflet-left" id="панель_кнопок" ref={pan_disable}>
            <div className={"leaflet-control leaflet-bar панель_кнопок_ячейка" + (ui.toolbar_window == "Help" ? " панель_кнопок_ячейка_нажата" : "" )} id="панель_кнопок_ячейка_справочная_информация" role="button" onClick={() => {ui.toolbar_window == "Help" ? ui.closeToolbarWindows() : ui.openHelpWindow()}} title={t("epidemics_window_help")} aria-label={t("epidemics_window_help")}>
                <BsQuestionLg className="панель_кнопок_кнопка" />
            </div>
            <div className={"leaflet-control leaflet-bar панель_кнопок_ячейка" + (ui.toolbar_window == "ModelSelection" ? " панель_кнопок_ячейка_нажата" : "" )} id="панель_кнопок_ячейка_выбор_модели" role="button" onClick={() => {ui.toolbar_window == "ModelSelection" ? ui.closeToolbarWindows() : ui.openModelSelectionWindow()}} title={t("epidemics_window_model_selection")} aria-label={t("epidemics_window_model_selection")}>
                <PiGraph className="панель_кнопок_кнопка" />
            </div>
            <div className={"leaflet-control leaflet-bar панель_кнопок_ячейка" + (ui.toolbar_window == "Layers" ? " панель_кнопок_ячейка_нажата" : "" )} id="панель_кнопок_ячейка_слои" role="button" onClick={() => {if (calculation.data != false) ui.toolbar_window == "Layers" ? ui.closeToolbarWindows() : ui.openLayersWindow();}} title={t("epidemics_window_layers")} aria-label={t("epidemics_window_layers")}>
                <LuLayers className={"панель_кнопок_кнопка " + (calculation.data == false && "выбор_кадра_кнопка_неактивная")} />
            </div>
        </div>
    );
});

const HelpWindow = observer(() => {
    const map = useMap();
    const pan_disable = useRef();
    const { t, i18n } = useTranslation();

    useEffect(() => {
        pan_disable.current.addEventListener("mouseenter", () => {map.doubleClickZoom.disable(); map.scrollWheelZoom.disable(); map.dragging.disable();});
        pan_disable.current.addEventListener("mouseleave", () => {map.doubleClickZoom.enable(); map.scrollWheelZoom.enable(); map.dragging.enable();});
        return () => {};
    }, [pan_disable]);

    return (
        <>
        <div className="leaflet-top leaflet-left" id="окно_справочная_информация">
            <div className="leaflet-control leaflet-bar окно_ячейка окно_ячейка_справочная_информация" ref={pan_disable}>
                <Container fluid>
                    <Row className="заголовок_окна">
                        <Col md="auto">{t("epidemics_window_help")}</Col>
                        <Col className="d-flex justify-content-end"><IoCloseSharp title={t("epidemics_close_window")} className="заголовок_окна_кнопка" role="button" onClick={() => {map.doubleClickZoom.enable(); map.scrollWheelZoom.enable(); map.dragging.enable(); ui.closeToolbarWindows();}} /></Col>
                    </Row>
                </Container>
                <Container fluid className="основная_часть_окна">
                    <Help.DistributedSEIR language={i18n.language} />
                </Container>
            </div>
        </div>
        </>
    )
});

const ModelSelectionWindow = observer(() => {
    const map = useMap();
    const pan_disable = useRef();
    const { t, i18n } = useTranslation();
    const [infection_options, setInfectionOptions] = useState([]);
    const [town_options, setTownOptions] = useState([]);
    const [calculation_options, setCalculationOptions] = useState(<option>{t("epidemics_loading")}</option>);
    const [revert_button, setRevertButton] = useState(false);

    const setModelParametersBackward = () => {
        for (const model_param of ["dSEIR_R0", "dSEIR_tE", "dSEIR_tI"]) ui.setModelParam(model_param, false);
        if (parseFloat(ui.model.dSEIR_sigma) > 0) ui.setModelParam("dSEIR_tE", (1 / parseFloat(ui.model.dSEIR_sigma)).toFixed(2));
        if (parseFloat(ui.model.dSEIR_gamma) > 0) {
            ui.setModelParam("dSEIR_tI", (1 / parseFloat(ui.model.dSEIR_gamma)).toFixed(2));
            if (parseFloat(ui.model.dSEIR_beta) > 0) ui.setModelParam("dSEIR_R0", (parseFloat(ui.model.dSEIR_beta) / parseFloat(ui.model.dSEIR_gamma)).toFixed(2));
        }
    }
    
    const setModelParametersForward = () => {
        for (const model_param of ["dSEIR_beta", "dSEIR_sigma", "dSEIR_gamma"]) ui.setModelParam(model_param, false);
        if (parseFloat(ui.model.dSEIR_tE) > 0) ui.setModelParam("dSEIR_sigma", (1 / parseFloat(ui.model.dSEIR_tE)).toFixed(4));
        if (parseFloat(ui.model.dSEIR_tI) > 0) {
            ui.setModelParam("dSEIR_gamma", (1 / parseFloat(ui.model.dSEIR_tI)).toFixed(4));
            if (parseFloat(ui.model.dSEIR_R0) > 0) ui.setModelParam("dSEIR_beta", (parseFloat(ui.model.dSEIR_R0) * parseFloat(ui.model.dSEIR_gamma)).toFixed(4));
        }
    }

    const loadCalculationList = async () => {
        const json = await getEpidemics_Calculation_List();
        if (json.length === 0 || json?.error) { ui.unloadModel(); animation.unload(); calculation.unloadData(); calculation.setStatus("status", "new"); }
        setCalculationOptions(json.map((item, i) => <option key={item["calculation_id"]} value={item["calculation_id"]}>{item["calculation_name"]}</option>))
    }

    const formatInfectionOptions = () => {
        let infection_optgroups = [];
        Object.keys(ui.infections[i18n.language]).forEach((infection_optgroup) => {
            let infection_options = [];
            ui.infections[i18n.language][infection_optgroup].forEach((item) => infection_options.push(<option key={item["infection_id"]} value={item["infection_id"]} data-dseir_r0={item["dSEIR_R0"]} data-dseir_te={item["dSEIR_tE"]} data-dseir_ti={item["dSEIR_tI"]}>{item["infection_name"]}</option>));
            infection_optgroups.push(<optgroup key={infection_optgroup} label={infection_optgroup}>{infection_options}</optgroup>)
        });
        setInfectionOptions(infection_optgroups);
    }

    const formatTownOptions = () => {
        let town_options = [];
        if (i18n.language == "en") ui.towns.forEach((item) => town_options.push({id: item["town_osm_rel"], label: transliterate(item["town_name"])}));
        else ui.towns.forEach((item) => town_options.push({id: item["town_osm_rel"], label: item["town_name"]}));
        setTownOptions(town_options);
    }

    useEffect(() => {
        pan_disable.current.addEventListener("mouseenter", () => {map.doubleClickZoom.disable(); map.scrollWheelZoom.disable(); map.dragging.disable();});
        pan_disable.current.addEventListener("mouseleave", () => {map.doubleClickZoom.enable(); map.scrollWheelZoom.enable(); map.dragging.enable();});
        return () => {};
    }, [pan_disable]);

    useEffect(() => {
        if (calculation.status.status == "done") calculation.setStatus("time_remaining", false);
        return () => {};
    }, [calculation.status.status]);

    useEffect(() => {
        if (ui.model.town) {
            if (ui.towns && ui.by_osm_rel && calculation.status.status == "new") {
                ui.setLocation("town_center", [ui.towns[ui.by_osm_rel[ui.model.town]].latitude, ui.towns[ui.by_osm_rel[ui.model.town]].longitude]);
                ui.setModelParam("outbreak", ui.location.town_center);
            }
            getEpidemics_Town_Boundaries(ui.model.town).then((data) => ui.setLocation("town_boundaries", data["boundaries"]));
        }
        else {
            ui.setModelParam("outbreak", false);
            ui.setLocation("town_boundaries", false);
        }
        return () => {};
    }, [ui.model.town]);    

    useEffect(() => {
        if (ui.infections) formatInfectionOptions();
        if (ui.towns) formatTownOptions();
        return () => {};
    }, [t]);

    useEffect(() => {
        calculation.setStatus("delete", false);
        if (!ui.infections) getEpidemics_Infection_List().then((json) => {ui.setInfections(json); formatInfectionOptions();});
        if (!ui.towns) getEpidemics_Town_List().then((json) => {ui.setTowns(json); formatTownOptions();});
        else formatTownOptions();
        loadCalculationList();
        loadCalculationMetadata(calculation.id).then(() => {setModelParametersBackward();});
        const status_timer = setInterval(() => {
            if (calculation.loading) return;
            if (calculation.id === 0) return;
            if (calculation.status.status === "in_progress") { loadCalculationList(); }
            if (["waiting", "in_progress"].includes(calculation.status.status)) loadCalculationMetadata(calculation.id);
        }, 5000);
        return () => {clearInterval(status_timer);};
    }, []);
    
    return (
        <>
        <div className="leaflet-top leaflet-left" id="окно_выбор_модели">
            <div className="leaflet-control leaflet-bar окно_ячейка окно_ячейка_выбор_модели" ref={pan_disable}>
                <Container fluid>
                    <Row className="заголовок_окна">
                        <Col md="auto">{t("epidemics_window_model_selection")}</Col>
                        <Col className="d-flex justify-content-end">
                            {(revert_button || calculation.status.status != "new") && <><PiLineVerticalLight className="заголовок_окна_кнопка"/>&nbsp;</>}
                            {revert_button && calculation.status.status == "new" && <><IoMdUndo title={t("epidemics_return_to_last_calculation")} className="заголовок_окна_кнопка" role="button" onClick={() => {ui.setModelParam("name", ""); loadCalculationMetadata().then(() => {setModelParametersBackward();})}} />&nbsp;
                            <PiLineVerticalLight className="заголовок_окна_кнопка"/>&nbsp;</>}
                            {calculation.status.status == "confirm_rename" && <><IoMdCheckmark title={t("epidemics_rename_calculation_confirm")} className="заголовок_окна_кнопка" role="button" onClick={ () => {
                                renameEpidemics_Calculation(calculation.id, ui.model.name).then( (res) => {
                                    if (res.success) {
                                        loadCalculationList();
                                        loadCalculationMetadata(calculation.id).then( () => {
                                            setModelParametersBackward();
                                        })
                                    } else {
                                        calculation.error_message = res.error;
                                        calculation.setStatus("status", "error");
                                    }
                                }) } } />&nbsp;
                            <PiLineVerticalLight className="заголовок_окна_кнопка"/>&nbsp;</> }
                            {calculation.status.delete && <><RiDeleteBin6Line title={t("epidemics_delete_calculation_confirm")} className="заголовок_окна_кнопка красный" role="button" onClick={ () => {
                                deleteEpidemics_Calculation(calculation.id).then( (res) => {
                                    if (res.success) {
                                        calculation.setStatus("delete", false);
                                        loadCalculationList();
                                        loadCalculationMetadata().then( () => {
                                            setModelParametersBackward();
                                        } )
                                    } else {
                                        calculation.error_message = res.error;
                                        calculation.setStatus("status", "error");
                                    }
                                } ) } } />&nbsp;
                            <PiLineVerticalLight className="заголовок_окна_кнопка"/>&nbsp;</> }
                            {(calculation.status.status == "confirm_rename" || calculation.status.delete) && <><IoMdUndo title={calculation.status.delete ? t("epidemics_delete_calculation_cancel") : t("epidemics_rename_calculation_cancel")} className="заголовок_окна_кнопка" role="button" onClick={() => {ui.setModelParam("name", ""); calculation.setStatus("delete", false); loadCalculationMetadata().then(() => {setModelParametersBackward();})}} />&nbsp;
                            <PiLineVerticalLight className="заголовок_окна_кнопка"/>&nbsp;</> }
                            {calculation.status.status == "done" && !calculation.status.delete && <><BiRename title={t("epidemics_rename_calculation")} className="заголовок_окна_кнопка" role="button" onClick={() => {ui.setModelParam("name", calculation.status.name); calculation.setStatus("status", "confirm_rename");}} />&nbsp;
                            <PiLineVerticalLight className="заголовок_окна_кнопка"/>&nbsp;</> }
                            {!["new", "confirm_rename"].includes(calculation.status.status) && !calculation.status.delete && <><RiDeleteBin6Line title={t("epidemics_delete_calculation")} className="заголовок_окна_кнопка" role="button" onClick={() => {calculation.setStatus("delete", true);}} />&nbsp;
                            <PiLineVerticalLight className="заголовок_окна_кнопка"/>&nbsp;</> }
                            <IoCloseSharp title={t("epidemics_close_window")} className="заголовок_окна_кнопка" role="button" onClick={() => {map.doubleClickZoom.enable(); map.scrollWheelZoom.enable(); map.dragging.enable(); ui.closeToolbarWindows();}} />   
                        </Col>
                    </Row>
                </Container>
                <Container fluid className="основная_часть_окна">
                    {!["new", "confirm_rename"].includes(calculation.status.status) && <Row><Col>
                        <Form.Select title={t("epidemics_calculation_select")} disabled={calculation.delete || ["waiting", "in_progress", "error"].includes(calculation.status.status)} onChange={e => {calculation.setId(e.target.value); if (e.target.value == 0) {setRevertButton(true); ui.unloadModel(); animation.unload(); calculation.unloadData(); calculation.setStatus("status", "new");} else {setRevertButton(false); loadCalculationMetadata(e.target.value).then(() => {setModelParametersBackward();})}}} value={calculation.id}>
                            <option key="0" value="0">{t("epidemics_new_calculation")}</option>
                            {calculation_options}
                        </Form.Select>
                    </Col></Row>}
                    {["new", "confirm_rename"].includes(calculation.status.status) && <Row><Col>
                        <Form.Control type="text" title={t("epidemics_new_calculation_tooltip")} onChange={e => {ui.setModelParam("name", e.target.value);}} value={ui.model.name} placeholder={calculation.status.status == "confirm_rename" ? t("epidemics_rename_calculation_placeholder") : t("epidemics_new_calculation_placeholder")} />
                    </Col></Row>}
                    <Row><Col>
                        <Form.Label>{t("epidemics_calculation_model_type")}</Form.Label>                
                        <Form.Select title={t("epidemics_calculation_model_type")} disabled={calculation.status.status != "new"} onChange={e => {ui.setModelParam("type", e.target.value);}} value={ui.model.type}>
                            <option key="dSEIR" value="dSEIR">{t("epidemics_calculation_model_type_dSEIR")}</option>
                        </Form.Select>
                    </Col></Row>
                    <Row><Col>
                        <Form.Label>{t("epidemics_calculation_infection")}</Form.Label>                
                        <Form.Select title={t("epidemics_calculation_infection")} disabled={calculation.status.status != "new"} onChange={e => {ui.setModelParam("infection", e.target.value); if (e.target.value != "other") {ui.setModelParam("dSEIR_R0", e.target.options[e.target.selectedIndex].dataset.dseir_r0); ui.setModelParam("dSEIR_tE", e.target.options[e.target.selectedIndex].dataset.dseir_te); ui.setModelParam("dSEIR_tI", e.target.options[e.target.selectedIndex].dataset.dseir_ti); setModelParametersForward();}}} value={ui.model.infection}>
                            <option key="other" value="other">{t("epidemics_calculation_infection_other")}</option>
                            {infection_options}
                        </Form.Select>
                    </Col></Row>
                    <Row><Col>
                        <Form.Label>{t("epidemics_calculation_town")}</Form.Label>
                        <Typeahead
                            id="epidemics_calculation_town" inputProps={{title: t("epidemics_calculation_town")}} emptyLabel={t("epidemics_calculation_town_not_found")}
                            options={town_options} paginate={false} disabled={calculation.status.status != "new"}
                            onChange={(selected) => {if (selected.length == 1) ui.setModelParam("town", selected[0]["id"]); else ui.setModelParam("town", false);}}
                            selected={(town_options.length > 0 && ui.model.town) ? [town_options[ui.by_osm_rel[ui.model.town]]] : []}
                            placeholder={calculation.status.status == "new" ? t("epidemics_calculation_town_placeholder") : t("epidemics_loading")} />
                    </Col></Row>
                    <Row>
                        <Col>
                            <Form.Label>R<sub>0</sub> {t("epidemics_calculation_dSEIR_R0_label")}</Form.Label>
                            <Form.Control type="number" title={t("epidemics_calculation_dSEIR_R0_tooltip")} disabled={calculation.status.status != "new"} onChange={e => {ui.setModelParam("dSEIR_R0", e.target.value); setModelParametersForward();}} step=".01" value={parseFloat(ui.model.dSEIR_R0)} />
                        </Col>
                        <Col>
                            <Form.Label>&rarr; {t("epidemics_calculation_dSEIR_beta_label")} = </Form.Label>
                            <Form.Control type="number" title={t("epidemics_calculation_dSEIR_beta_tooltip")} disabled={calculation.status.status != "new"} onChange={e => {ui.setModelParam("dSEIR_beta", e.target.value); setModelParametersBackward();}} step=".0001" value={parseFloat(ui.model.dSEIR_beta)} />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Label>{t("epidemics_calculation_dSEIR_tE_label")}</Form.Label>
                            <Form.Control type="number" title={t("epidemics_calculation_dSEIR_tE_tooltip")} disabled={calculation.status.status != "new"} onChange={e => {ui.setModelParam("dSEIR_tE", e.target.value); setModelParametersForward();}} step=".01" value={parseFloat(ui.model.dSEIR_tE)} placeholder={t("epidemics_calculation_dSEIR_placeholder_days")} />
                        </Col>
                        <Col>
                            <Form.Label>&rarr; {t("epidemics_calculation_dSEIR_sigma_label")} = </Form.Label>
                            <Form.Control type="number" title={t("epidemics_calculation_dSEIR_sigma_tooltip")} disabled={calculation.status.status != "new"} onChange={e => {ui.setModelParam("dSEIR_sigma", e.target.value); setModelParametersBackward();}} step=".0001" value={parseFloat(ui.model.dSEIR_sigma)} />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Label>{t("epidemics_calculation_dSEIR_tI_label")}</Form.Label>
                            <Form.Control type="number" title={t("epidemics_calculation_dSEIR_tI_tooltip")} disabled={calculation.status.status != "new"} onChange={e => {ui.setModelParam("dSEIR_tI", e.target.value); setModelParametersForward();}} step=".01" value={parseFloat(ui.model.dSEIR_tI)} placeholder={t("epidemics_calculation_dSEIR_placeholder_days")} />
                        </Col>
                        <Col>
                            <Form.Label>&rarr; {t("epidemics_calculation_dSEIR_gamma_label")} = </Form.Label>
                            <Form.Control type="number" title={t("epidemics_calculation_dSEIR_gamma_tooltip")} disabled={calculation.status.status != "new"} onChange={e => {ui.setModelParam("dSEIR_gamma", e.target.value); setModelParametersBackward();}} step=".0001" value={parseFloat(ui.model.dSEIR_gamma)} />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Label>{t("epidemics_calculation_dSEIR_radius_label")}</Form.Label>
                            <Form.Control type="number" title={t("epidemics_calculation_dSEIR_radius_tooltip")} disabled="true" onChange={e => {ui.setModelParam("dSEIR_radius", e.target.value);}} step=".01" value={parseFloat(ui.model.dSEIR_radius)} placeholder={t("epidemics_calculation_dSEIR_placeholder_km")} />
                        </Col>
                        <Col className="d-grid gap-2">
                            <Form.Label>
                                {calculation.status.status == "new" && t("epidemics_status_new_label")}
                                {calculation.status.status == "waiting" && t("epidemics_status_waiting_label")}
                                {calculation.status.status == "in_progress" && (calculation.status.time_remaining ? t("epidemics_status_waiting_label_remaining") + " \u223C" + (calculation.status.time_remaining > 60 ? Math.round(parseInt(calculation.status.time_remaining) / 60) + " " + t("epidemics_status_waiting_label_remaining_min") : calculation.status.time_remaining + " " + t("epidemics_status_waiting_label_remaining_sec")) : t("epidemics_status_waiting_label"))}
                                {["done", "confirm_rename"].includes(calculation.status.status) && t("epidemics_status_done_label")}
                                {calculation.status.status == "error" && t("epidemics_status_error_label")}
                            </Form.Label>
                            <Button
                                variant={calculation.status.status == "error" ? "danger" : (["waiting", "in_progress"].includes(calculation.status.status) ? "warning" : "success")}
                                className="основная_часть_окна_кнопка"
                                disabled={calculation.loading || calculation.delete || ["waiting", "in_progress", "confirm_rename"].includes(calculation.status.status) || calculation.status.status == "new" && Object.values(ui.model).some(value => value == false)}
                                title={calculation.error_message ? `Ошибка: ${calculation.error_message}` : undefined}
                                onClick={() => {
                                    if (calculation.status.status == "new") {
                                        calculation.loading = true;
                                        calculation.setStatus("status", "waiting");
                                        calculation.error_message = "";
                                        performEpidemics_Calculation(ui.model.name, ui.model.type, ui.model.infection, ui.model.town, ui.model.outbreak, ui.model.dSEIR_beta, ui.model.dSEIR_sigma, ui.model.dSEIR_gamma, ui.model.dSEIR_radius).then((res) => {
                                            if (res.success) {
                                                calculation.loading = false;
                                                calculation.error_message = "";
                                            } else {
                                                calculation.loading = false;
                                                calculation.setId(0);
                                                calculation.setStatus("status", "new");
                                                calculation.setStatus("status", "error");
                                                calculation.error_message = res.error;
                                            }
                                        });
                                    } else loadCalculationData(calculation.id).then(() => {ui.closeToolbarWindows();})}}
                            >
                                {(calculation.loading || ["waiting", "in_progress"].includes(calculation.status.status)) && <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />}
                                {calculation.status.status == "new" && t("epidemics_status_new_button")}
                                {calculation.status.status == "waiting" && t("epidemics_status_waiting_button")}
                                {calculation.status.status == "in_progress" && t("epidemics_status_in_progress_button") + " (" + calculation.status.frame_num + ")"}
                                {["done", "confirm_rename"].includes(calculation.status.status) && (calculation.loading ? "\u00A0\u00A0" + t("epidemics_loading") : t("epidemics_status_done_button"))}
                                {calculation.status.status == "error" && t("epidemics_status_error_button")}
                            </Button>
                            {calculation.error_message && (
                                <div className="text-danger mt-2" style={{fontSize: '0.8rem'}}>
                                    {t("epidemics_error_label")}: {calculation.error_message}
                                </div>
                            )}
                        </Col>
                    </Row>
                </Container>
            </div>
        </div>
        </>
    );
})

const LayersWindow = observer(() => {
    const map = useMap();
    const pan_disable = useRef();
    const { t } = useTranslation();

    useEffect(() => {
        pan_disable.current.addEventListener("mouseenter", () => {map.doubleClickZoom.disable(); map.scrollWheelZoom.disable(); map.dragging.disable();});
        pan_disable.current.addEventListener("mouseleave", () => {map.doubleClickZoom.enable(); map.scrollWheelZoom.enable(); map.dragging.enable();});
        return () => {};
    }, [pan_disable]);

    let layers_radiolist = "";
    if (calculation.layers) layers_radiolist = calculation.layers.map((layer, i) => 
        <Form.Check>
            <Form.Check.Input type="radio" name="layers" id={("layers" + i)} value={i} checked={animation.layer == i} onClick={() => animation.setLayer(i)} />
            <Form.Check.Label for={("layers" + i)} style={{color: layer["цвет_линии"]}} onClick={() => animation.setLayer(i)}>{t("epidemics_layer_" + layer["описание"])}</Form.Check.Label>
        </Form.Check>);

    return (
        <>
        <div className="leaflet-top leaflet-left" id="окно_слои">
            <div className="leaflet-control leaflet-bar окно_ячейка окно_ячейка_слои" ref={pan_disable}>
                <Container fluid>
                    <Row className="заголовок_окна">
                        <Col md="auto">{t("epidemics_window_layers")}</Col>
                        <Col className="d-flex justify-content-end"><IoCloseSharp title={t("epidemics_close_window")} className="заголовок_окна_кнопка" role="button" onClick={() => {map.doubleClickZoom.enable(); map.scrollWheelZoom.enable(); map.dragging.enable(); ui.closeToolbarWindows();}} /></Col>
                    </Row>
                </Container>
                <Container fluid className="основная_часть_окна">
                    {layers_radiolist}
                </Container>
                <div className="invisible">{animation.layer}</div>
            </div>
        </div>
        </>
    );
});

const SEIRGraph = observer(() => {
    const map = useMap();
    const pan_disable = useRef();
    const { t } = useTranslation();

    useEffect(() => {
        pan_disable.current.addEventListener("mouseenter", () => {map.doubleClickZoom.disable(); map.scrollWheelZoom.disable(); map.dragging.disable();});
        pan_disable.current.addEventListener("mouseleave", () => {map.doubleClickZoom.enable(); map.scrollWheelZoom.enable(); map.dragging.enable();});
        return () => {};
    }, [pan_disable]);

    let chart_lines = "";
    chart_lines = calculation.layers.map((layer, i) => 
        <Line type="monotone" isAnimationActive={false} dataKey={layer["ключ"]} stroke={layer["цвет_линии"]} strokeDasharray={(i == animation.layer ? "" : "1 1")} strokeWidth={(i == animation.layer ? 2 : 1)} dot={false} />);

    return (
        <>
            <div className="leaflet-top leaflet-right" id="график">
                <div className="leaflet-control leaflet-bar" id="график_ячейка" ref={pan_disable}>
                    <LineChart width={450} height={350} data={calculation.data["динамика"]} margin={{top: 12, right: 28, left: 0, bottom: 20}}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" dataKey="x" scale="linear" interval={13} domain={[1, animation.maxFrame]}><Label dy={15} value={t("epidemics_day")} /></XAxis>
                        <YAxis domain={[0, 1]} tickCount={6}><Label dx={-8} angle={270} value={t("epidemics_fraction")} /><Label style={{fontSize: "medium", fill: calculation.layers[animation.layer]["цвет_линии"]}} dx={0} dy={164} value={calculation.data["динамика"][animation.frame-1][calculation.layers[animation.layer]["ключ"]].toFixed(3)} /></YAxis>
                        <Legend width={396} wrapperStyle={{marginTop: "-6px", marginLeft: "45px"}} align="center" verticalAlign="top" />
                        {chart_lines}
                        <ReferenceLine stroke={calculation.layers[animation.layer]["цвет_линии"]} segment={[{"x": animation.frame, "y": 0}, {"x": animation.frame, "y": calculation.data["динамика"][animation.frame-1][calculation.layers[animation.layer]["ключ"]]}]} />
                        <ReferenceDot x={animation.frame} y={calculation.data["динамика"][animation.frame-1][calculation.layers[animation.layer]["ключ"]]} r={3} stroke={calculation.layers[animation.layer]["цвет_линии"]} strokeWidth={2} fill="#ffffff" isFront={true} />
                    </LineChart>
                </div>
            </div>
        </>
    );    
});

const MapView = observer(() => {
    const [convex_hulls, setConvexHulls] = useState();
    const [town_boundaries, setTownBoundaries] = useState(false);
    const map = useMapEvents({
        moveend() {
            if (calculation.status.status == "new") getEpidemics_Town_Convex_Hulls(map.getBounds()).then((data) => {
                let convex_hulls = [];
                data.forEach((item) => {if (item["town_osm_rel"] != ui.model.town) convex_hulls.push(<Polygon pathOptions={{color: "", fillColor: "#3388FF"}} positions={item["convex_hull"]} eventHandlers={{click: () => {ui.setModelParam("town", item["town_osm_rel"]);}}} />)});
                setConvexHulls(convex_hulls);
            });
            else setConvexHulls();   
        }
    });

    useEffect(() => {
        if (calculation.status.status != "new") setConvexHulls();
        return () => {};
    }, [calculation.status.status]);

    useEffect(() => {
        map.setView(ui.location.town_center, map.getZoom());
        return () => {};
    }, [ui.location.town_center]);

    useEffect(() => {
        if (ui.location.town_boundaries) {setTownBoundaries(ui.location.town_boundaries.map((line) => <Polyline positions={line} />));}
        else {setTownBoundaries(false); map.fitBounds(map.getBounds());}
        return () => {};
    }, [ui.location.town_boundaries]);

    return(<>{convex_hulls}{town_boundaries}</>);
});

const Epidemics_Map_Page = observer(() => {
    const markerRef = useRef();
    const { t } = useTranslation();

    useEffect(() => {
        document.querySelector(".leaflet-control-zoom-in").setAttribute("title", t("epidemics_zoom_in_button"));
        document.querySelector(".leaflet-control-zoom-in").setAttribute("aria-label", t("epidemics_zoom_in_button"));
        document.querySelector(".leaflet-control-zoom-out").setAttribute("title", t("epidemics_zoom_out_button"));
        document.querySelector(".leaflet-control-zoom-out").setAttribute("aria-label", t("epidemics_zoom_out_button"));
        return () => {};
    }, [t]);

    useEffect(() => {
        const marker = markerRef.current;
        if (marker != null && calculation.status.status == "new") marker.openPopup();
        return () => {};
    }, [ui.model.outbreak]);

    const marker_icon = new Icon.Default();
    marker_icon.options.iconUrl = "/Epidemics/marker-icon.png";
    marker_icon.options.iconRetinaUrl = "/Epidemics/marker-icon-2x.png";
    marker_icon.options.shadowUrl = "/Epidemics/marker-shadow.png";

    const markerEvents = useMemo(() => ({
        dragend() {
            const marker = markerRef.current;
            if (marker != null) {
                ui.setModelParam("outbreak", [marker.getLatLng().lat, marker.getLatLng().lng]);
        }},
    }), []);

    return (
        <>
        <MapContainer center={ui.location.town_center} zoom={13} scrollWheelZoom={true}>
        <MapView center={ui.location.town_center} zoom={13} />
        <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {ui.model.outbreak && <Marker
            position={ui.model.outbreak} icon={marker_icon} draggable={calculation.status.status == "new"}
            ref={markerRef} eventHandlers={markerEvents}>
            {calculation.status.status == "new" && <Popup>{t("epidemics_calculation_initial_outbreak")}</Popup>}
        </Marker>}
        {calculation.data && <HeatmapLayer
            fitBoundsOnLoad
            fitBoundsOnUpdate
            points={calculation.data["динамика_по_точкам"]}
            longitudeExtractor={m => m[1]}
            latitudeExtractor={m => m[0]}
            intensityExtractor={m => parseFloat(m[2][animation.layer][animation.frame-1])}
            max={1.0}
            useLocalExtrema={false}
            radius={10}
            opacity={0.7}
            gradient={{0.2: "blue", 0.4: "cyan", 0.6: "lime", 0.8: "yellow", 1.0: "red"}} />
        }
        <FrameSlider />
        <Toolbar />
        {ui.toolbar_window == "Help" && <HelpWindow />}
        {ui.toolbar_window == "ModelSelection" && <ModelSelectionWindow />}
        {ui.toolbar_window == "Layers" && <LayersWindow />}
        {calculation.layers[animation.layer] !== undefined && calculation.data && <SEIRGraph />}
        <div className="invisible">{animation.layer}{animation.frame}</div>
        </MapContainer>
        </>
    );
});

export default Epidemics_Map_Page;

